import apiClient from '@/lib/axios';
import type {
  ApiResponse,
  InsuranceCompany,
  InsuranceLookup,
  InsuranceCompanyFormData,
  InsuranceAdjustor,
  AdjustorLookup,
  InsuranceAdjustorFormData,
} from '../types';

// #region Insurance Companies

/**
 * Get all insurance companies
 * @param isActive - Optional filter: true for active only, false for inactive only, undefined for all
 */
export const getInsuranceCompanies = async (
  isActive?: boolean
): Promise<InsuranceCompany[]> => {
  const params = isActive !== undefined ? { isActive } : undefined;
  const response = await apiClient.get<ApiResponse<InsuranceCompany[]>>(
    '/insurance-companies',
    { params }
  );
  return response.data.data;
};

/**
 * Get a single insurance company by ID
 */
export const getInsuranceCompany = async (id: number): Promise<InsuranceCompany> => {
  const response = await apiClient.get<ApiResponse<InsuranceCompany>>(
    `/insurance-companies/${id}`
  );
  return response.data.data;
};

/**
 * Create a new insurance company
 */
export const createInsuranceCompany = async (
  data: InsuranceCompanyFormData
): Promise<InsuranceCompany> => {
  const response = await apiClient.post<ApiResponse<InsuranceCompany>>(
    '/insurance-companies',
    data
  );
  return response.data.data;
};

/**
 * Update an existing insurance company
 */
export const updateInsuranceCompany = async (
  id: number,
  data: InsuranceCompanyFormData
): Promise<InsuranceCompany> => {
  const response = await apiClient.put<ApiResponse<InsuranceCompany>>(
    `/insurance-companies/${id}`,
    data
  );
  return response.data.data;
};

/**
 * Delete (soft-delete) an insurance company
 */
export const deleteInsuranceCompany = async (id: number): Promise<void> => {
  await apiClient.delete(`/insurance-companies/${id}`);
};

/**
 * Get insurance company lookup for dropdowns (active only)
 */
export const getInsuranceCompanyLookup = async (): Promise<InsuranceLookup[]> => {
  const response = await apiClient.get<ApiResponse<InsuranceLookup[]>>(
    '/insurance-companies/lookup'
  );
  return response.data.data;
};

// #endregion

// #region Insurance Adjustors

/**
 * Get all adjustors for an insurance company
 * @param companyId - The company ID
 * @param isActive - Optional filter: true for active only, false for inactive only, undefined for all
 */
export const getAdjustors = async (
  companyId: number,
  isActive?: boolean
): Promise<InsuranceAdjustor[]> => {
  const params = isActive !== undefined ? { isActive } : undefined;
  const response = await apiClient.get<ApiResponse<InsuranceAdjustor[]>>(
    `/insurance-companies/${companyId}/adjustors`,
    { params }
  );
  return response.data.data;
};

/**
 * Get a single adjustor by ID
 */
export const getAdjustor = async (id: number): Promise<InsuranceAdjustor> => {
  const response = await apiClient.get<ApiResponse<InsuranceAdjustor>>(
    `/insurance-adjustors/${id}`
  );
  return response.data.data;
};

/**
 * Create a new adjustor for a company
 */
export const createAdjustor = async (
  companyId: number,
  data: InsuranceAdjustorFormData
): Promise<InsuranceAdjustor> => {
  const response = await apiClient.post<ApiResponse<InsuranceAdjustor>>(
    `/insurance-companies/${companyId}/adjustors`,
    data
  );
  return response.data.data;
};

/**
 * Update an existing adjustor
 */
export const updateAdjustor = async (
  id: number,
  data: InsuranceAdjustorFormData
): Promise<InsuranceAdjustor> => {
  const response = await apiClient.put<ApiResponse<InsuranceAdjustor>>(
    `/insurance-adjustors/${id}`,
    data
  );
  return response.data.data;
};

/**
 * Delete (soft-delete) an adjustor
 */
export const deleteAdjustor = async (id: number): Promise<void> => {
  await apiClient.delete(`/insurance-adjustors/${id}`);
};

/**
 * Get adjustor lookup for dropdowns (active only for a company)
 */
export const getAdjustorLookup = async (companyId: number): Promise<AdjustorLookup[]> => {
  const response = await apiClient.get<ApiResponse<AdjustorLookup[]>>(
    `/insurance-companies/${companyId}/adjustors/lookup`
  );
  return response.data.data;
};

// #endregion
